import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Mail, Loader2, Ban, RotateCw, ShieldAlert } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', slate: '#5C6B7A', green: '#1D9E75', amber: '#E8A020', red: '#E24B4A', bg: '#F4F6F9' };
const STATUS = { pending: C.blue, accepted: C.green, expired: C.slate, voided: C.slate, superseded: C.slate };

export function LoginInvitesCard({ api }) {
  const [email, setEmail] = useState('');
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState('');

  const load = () => api.get('/access/login-invites').then(r => setRows(r.data.invites)).catch(() => setRows([]));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const issue = async (addr) => {
    setBusy(addr);
    try {
      const { data } = await api.post('/access/login-invites', { email: addr });
      if (data.email_status === 'sent') {
        toast.success(`Invite sent to ${addr}${data.starting_password_retired ? ' — the starting password no longer works' : ''}`);
      } else {
        toast.warning(data.email_note || `Invite created but not delivered (${data.email_status})`, { duration: 9000 });
      }
      setEmail('');
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not issue the invite');
    }
    setBusy('');
  };

  const voidInvite = async (id) => {
    setBusy(id);
    try {
      await api.post(`/access/login-invites/${id}/void`);
      toast.success('Link voided — it will not open again');
      load();
    } catch { toast.error('Could not void that invite'); }
    setBusy('');
  };

  return (
    <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="login-invites-card">
      <div className="flex items-center gap-2 mb-1">
        <Mail className="w-4 h-4" style={{ color: C.blue }} />
        <h3 className="text-sm font-semibold" style={{ color: C.navy }}>Login Invites</h3>
      </div>
      <p className="text-[11px] mb-3" style={{ color: C.slate }}>
        Hand over an account, never a password. The invitee gets a single-use link, sets their own password and
        is walked into authenticator enrolment. Links are stored hashed, expire in 72 hours, die on first use,
        and issuing one retires any starting password that was passed on by hand.
      </p>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <input value={email} onChange={e => setEmail(e.target.value)} placeholder="email of an existing account"
          data-testid="invite-email-input"
          className="px-2 py-1.5 text-xs rounded border flex-1 min-w-[220px]"
          style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.navy }} />
        <Button onClick={() => issue(email.trim().toLowerCase())} disabled={!email.includes('@') || !!busy}
          data-testid="invite-send-btn" className="text-xs rounded-md text-white h-8" style={{ background: C.blue }}>
          {busy === email.trim().toLowerCase() ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Send invite link'}
        </Button>
      </div>

      {rows === null ? (
        <p className="text-[11px]" style={{ color: C.slate }}>Loading invites…</p>
      ) : !rows.length ? (
        <p className="text-[11px]" style={{ color: C.slate }} data-testid="invites-empty">No invites issued yet.</p>
      ) : (
        <table className="w-full text-xs" data-testid="invites-table">
          <thead><tr className="text-left text-[10px] uppercase" style={{ color: C.slate }}>
            <th className="py-1 pr-2">Account</th><th className="py-1 pr-2">Role</th><th className="py-1 pr-2">Status</th>
            <th className="py-1 pr-2">Delivery</th><th className="py-1 pr-2">Expires</th><th className="py-1 pr-2">Issued by</th>
            <th className="py-1"></th>
          </tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.invite_id} className="border-t" style={{ borderColor: 'rgba(136,150,168,0.12)' }}
                data-testid={`invite-row-${r.email}`}>
                <td className="py-1.5 pr-2" style={{ color: C.navy }}>{r.name}<span className="ml-1 text-[10px]" style={{ color: C.slate }}>{r.email}</span></td>
                <td className="py-1.5 pr-2 text-[10px]" style={{ color: C.slate }}>{(r.role || '').replace(/_/g, ' ')}</td>
                <td className="py-1.5 pr-2">
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase"
                    style={{ background: (STATUS[r.status] || C.slate) + '15', color: STATUS[r.status] || C.slate }}>
                    {r.status}
                  </span>
                </td>
                <td className="py-1.5 pr-2 text-[10px]" style={{ color: r.email_status === 'sent' ? C.green : C.amber }}>
                  {r.email_status || '—'}
                  {r.email_note && <ShieldAlert className="w-3 h-3 inline ml-1" title={r.email_note} />}
                </td>
                <td className="py-1.5 pr-2 text-[10px]" style={{ color: C.slate }}>{(r.expires_at || '').slice(0, 16).replace('T', ' ')}</td>
                <td className="py-1.5 pr-2 text-[10px]" style={{ color: C.slate }}>{r.issued_by}</td>
                <td className="py-1.5 text-right whitespace-nowrap">
                  {r.status === 'pending' && (
                    <Button size="sm" variant="ghost" onClick={() => voidInvite(r.invite_id)} disabled={!!busy}
                      className="h-6 px-1.5 text-[10px]" style={{ color: C.red }} data-testid={`invite-void-${r.email}`}>
                      <Ban className="w-3 h-3 mr-1" />Void
                    </Button>
                  )}
                  {r.status !== 'accepted' && (
                    <Button size="sm" variant="ghost" onClick={() => issue(r.email)} disabled={!!busy}
                      className="h-6 px-1.5 text-[10px]" style={{ color: C.blue }} data-testid={`invite-resend-${r.email}`}>
                      <RotateCw className="w-3 h-3 mr-1" />{r.status === 'pending' ? 'Reissue' : 'Invite again'}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {(rows || []).some(r => r.email_note) && (
        <p className="text-[10px] mt-2 p-2 rounded-sm" style={{ background: C.amber + '10', color: '#8A6212' }}
          data-testid="invite-held-note">
          {(rows || []).find(r => r.email_note).email_note}
        </p>
      )}
    </Card>
  );
}
